<script setup lang="ts">
import { computed, ref, watch, onMounted, nextTick } from 'vue';
import {
  iTextInput,
  iButton,
  iButtonStyles,
  iButtonTypes,
  iButtonSizes,
} from '@inscribe/signal';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import { registerChatAgentHookClickEvent } from '@/events-logger';

const DEFAULT_TEXTAREA_HEIGHT = 44;

/** Global modules */
const store = useStore();
const route = useRoute();

/** Refs */
const userInput = ref('');

/** Derived state */
const chatPanelIsOpen = computed(() => store.state.chatStore.chatIsOpen);
const currentConversationUuid = computed(
  () => store.getters.currentConversationUuid,
);
const hasExistingMessages = computed(() => {
  const messages = store.getters.getMessagesForConversationUuid(
    currentConversationUuid.value,
  );
  return messages && messages.length > 0;
});

const isMac = computed(() => {
  return navigator.userAgent.includes('Mac');
});

/** watchers */
watch(chatPanelIsOpen, (isOpen) => {
  if (!isOpen) {
    resetPromptBoxState();
  }
});

watch(userInput, () => {
  autoResizeTextarea();
});

/** Handlers */
function autoResizeTextarea() {
  const textInputEl = document.querySelector<HTMLTextAreaElement>(
    '[data-testid="chat-floating-prompt-box-textarea"] textarea',
  );
  if (textInputEl) {
    // If empty, always use default height
    if (!userInput.value.trim()) {
      textInputEl.style.height = `${DEFAULT_TEXTAREA_HEIGHT}px`;
      return;
    }

    // Then set to the larger of scrollHeight or default height
    const newHeight = Math.max(
      textInputEl.scrollHeight,
      DEFAULT_TEXTAREA_HEIGHT,
    );
    textInputEl.style.height = `${newHeight}px`;
  }
}

function resetPromptBoxState() {
  userInput.value = '';

  // Automatically focus the text input if it exists and there are no messages
  const textInputEl = document.querySelector<HTMLTextAreaElement>(
    '[data-testid="chat-floating-prompt-box-textarea"] textarea',
  );
  if (textInputEl && !hasExistingMessages.value) {
    nextTick(() => {
      textInputEl.focus();
    });
  }
}

function askQuestion() {
  // Create a new conversation whenever a question is asked via this floating
  // prompt box, since otherwise you are potentially adding to previous
  // unrelated context.
  store.dispatch('createNewConversationUuid');
  store.dispatch('openChat');

  const customerId = route.params.customer_id as string | undefined;
  const documentId = route.params.document_id as string | undefined;

  store.dispatch('submitConversationMessage', {
    conversationUuid: store.getters.currentConversationUuid,
    userMessageContent: userInput.value,
    contextCustomerUuids: customerId ? [customerId] : [],
    contextDocumentIds: documentId ? [documentId] : [],
  });
  registerChatAgentHookClickEvent('customer-floating-prompt-box');
}

function onInput(value: string) {
  userInput.value = value;
}

function onPromptBoxClick() {
  // If there's an existing conversation with messages, open the chat panel immediately
  if (hasExistingMessages.value && !chatPanelIsOpen.value) {
    store.dispatch('openChat');
  }
}

/** Lifecycle */
onMounted(() => {
  resetPromptBoxState();
  autoResizeTextarea();
});

defineExpose({
  askQuestion,
  chatPanelIsOpen,
  iButtonStyles,
  iButtonTypes,
  iButtonSizes,
  userInput,
});
</script>

<template>
  <div
    class="chat-floating-prompt-box i-p-2 i-radius-3xl"
    :class="{ 'is-open': !chatPanelIsOpen }"
    @click="onPromptBoxClick"
  >
    <i-text-input
      data-testid="chat-floating-prompt-box-textarea"
      placeholder="Ask a question"
      mode="textarea"
      :prevent-default-enter="true"
      :value="userInput"
      @on-input-enter="askQuestion"
      @input="onInput"
    ></i-text-input>
    <div v-if="!userInput.trim()" class="keyboard-shortcut i-text-body-3">
      <span v-if="isMac" class="command-symbol">⌘</span
      ><span v-else>Ctrl+</span>I
    </div>
    <i-button
      :button-style="iButtonStyles.SOLID_DARK"
      :size="iButtonSizes.SMALL"
      :type="iButtonTypes.NEUTRAL"
      :disabled="!userInput?.trim()"
      left-icon="medium-arrows-up"
      class="float-button"
      @button-click="askQuestion"
    ></i-button>
  </div>
</template>

<style lang="scss" scoped>
.chat-floating-prompt-box {
  overflow: hidden;
  transition:
    height 150ms ease-in-out,
    max-height 150ms ease-in-out,
    bottom 150ms ease-in-out,
    transform 200ms ease-in-out;
  position: fixed;
  background-color: var(--surface-neutral-pressed);
  bottom: -20%;
  left: 50%;
  margin-left: 132px; // left nav width / 2
  width: 400px;
  z-index: 1000;
  transform: translateX(-50%);

  &:hover {
    transform: translateX(-50%) scale(1.05);
  }

  &.is-open {
    bottom: 16px;
  }

  .keyboard-shortcut {
    position: absolute;
    right: 52px; // button width (~40px) + gap
    bottom: 18px;
    font-family: 'Roboto Mono';
    color: var(--text-neutral);
    pointer-events: none;
  }

  .float-button {
    position: absolute;
    right: 12px;
    bottom: 12px;
  }

  :deep(.i-text-input) {
    height: auto;

    .input-wrapper {
      min-height: auto; // need to reset the one from Design System
      height: auto; // need to reset the one from Design System

      border: 0;
      border-radius: 16px;

      &::after {
        border: 0;
      }

      textarea {
        padding: 12px;
        padding-left: 16px;
        padding-top: 12px;
        border-radius: 16px;
        overflow: hidden;
        resize: none; /* let JS handle height */
        margin-right: 68px;
        transition: height 150ms ease-in-out;
        max-height: 200px;
        overflow-y: auto;
      }
    }
  }
}
</style>
